import React, { useMemo, useState } from 'react';
import {
  ArrowDownWideNarrow,
  ArrowUpWideNarrow,
  ChevronsUpDown
} from 'lucide-react';

interface Props {
  data: any[];
  visibleColumns: string[];
}

const DynamicDataTable: React.FC<Props> = ({ data, visibleColumns }) => {
  const [globalSearch, setGlobalSearch] = useState('');
  const [filters, setFilters] = useState<{ [key: string]: string }>({});
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const filteredData = useMemo(() => {
    let result = [...data];

    if (globalSearch) {
      result = result.filter((row) =>
        visibleColumns.some((key) => String(row[key]).toLowerCase().includes(globalSearch.toLowerCase()))
      );
    }

    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        result = result.filter((row) =>
          String(row[key]).toLowerCase().includes(value.toLowerCase())
        );
      }
    });

    if (sortConfig) {
      result.sort((a, b) => {
        const valA = a[sortConfig.key];
        const valB = b[sortConfig.key];
        if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
        if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [data, globalSearch, filters, sortConfig, visibleColumns]);

  const totalPages = Math.ceil(filteredData.length / pageSize);
  const paginatedData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, page, pageSize]);

  const handleSort = (key: string) => {
    setSortConfig((prev) =>
      prev?.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' }
    );
  };

  return (
    <div className="transition-all duration-300 p-4 max-w-[1200px] mx-auto border rounded-lg shadow-md bg-white">
      <div className="mb-4">
        <input
          type="text"
          placeholder="Global search..."
          value={globalSearch}
          onChange={(e) => {
            setGlobalSearch(e.target.value);
            setPage(1);
          }}
          className="border px-3 py-2 rounded-md shadow-sm w-full md:w-1/3"
        />
      </div>

      <div className="overflow-x-auto border rounded-lg max-h-[500px] overflow-y-auto">
        <table className="min-w-full table-auto border-collapse text-sm text-gray-800">
          <thead className="bg-gray-100 sticky top-0 z-10">
            <tr>
              {visibleColumns.map((key) => (
                <th
                  key={key}
                  onClick={() => handleSort(key)}
                  className="px-4 py-2 border cursor-pointer whitespace-nowrap bg-gray-100"
                >
                  <div className="flex justify-between items-center">
                    {key.toUpperCase()}
                    {sortConfig?.key === key ? (
                      sortConfig.direction === 'asc' ? (
                        <ArrowUpWideNarrow className="w-4 h-4 ml-1" />
                      ) : (
                        <ArrowDownWideNarrow className="w-4 h-4 ml-1" />
                      )
                    ) : (
                      <ChevronsUpDown className="w-4 h-4 ml-1 text-gray-400" />
                    )}
                  </div>
                  <input
                    type="text"
                    placeholder="Filter"
                    value={filters[key] || ''}
                    onChange={(e) => {
                      setFilters({ ...filters, [key]: e.target.value });
                      setPage(1);
                    }}
                    className="mt-1 w-full px-2 py-1 border rounded-md text-xs"
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white">
            {paginatedData.map((row, rowIndex) => (
              <tr key={rowIndex} className="border-t hover:bg-gray-50">
                {visibleColumns.map((key) => (
                  <td key={key} className="px-4 py-2 border whitespace-nowrap">
                    {String(row[key])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-between items-center mt-4">
        <div className="text-sm text-gray-600">
          Page {page} of {totalPages || 1}
        </div>
        <div className="flex gap-2 items-center">
          <button
            onClick={() => setPage((p) => Math.max(p - 1, 1))}
            className="px-3 py-1 border border-gray-300 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50"
            disabled={page === 1}
          >
            Prev
          </button>
          <button
            onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
            className="px-3 py-1 border border-gray-300 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50"
            disabled={page === totalPages}
          >
            Next
          </button>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
            className="px-3 py-1 border border-gray-300 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
          >
            {[5, 10, 20, 50].map((size) => (
              <option key={size} value={size}>
                {size} rows
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};

export default DynamicDataTable;
